import { ArrowDown, ArrowUp, ChevronsUpDown, ChevronRight } from 'lucide-react'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import type { ProductBadge } from '../segments'
import type { ProductRecord, SortKey } from '../types'
import { deltaRate, integer, itemUnitPrice, metricsAcrossDates, metricsOn, money, percent, previousDateOf } from '../utils'

interface ProductTableProps {
  products: ProductRecord[]
  selectedDate: string
  dates: string[]
  sortKey?: SortKey
  sortDirection?: 'asc' | 'desc'
  onSort?: (key: SortKey) => void
  compact?: boolean
  emptyMessage?: string
  metricDates?: string[]
  showDelta?: boolean
  showAverageItemPrice?: boolean
  badgesByProductId?: Map<string, ProductBadge[]>
  matchTextByProductId?: Map<string, string>
  profitByProductId?: Map<string, number | null>
  profitMarginByProductId?: Map<string, number | null>
  profitContextByProductId?: Map<string, string>
  profitLabel?: string
  profitMarginLabel?: string
  erpAvailableByMerchantSku?: ReadonlyMap<string, number>
  detailSearch?: string
}

const columns: Array<{ key: SortKey, label: string, className?: string }> = [
  { key: 'gmv', label: '成交金额' },
  { key: 'soldItemCount', label: '成交件数' },
  { key: 'averageItemPrice', label: '件单价' },
  { key: 'productVisitors', label: '访客', className: 'column-secondary' },
  { key: 'conversionRate', label: '转化率' },
  { key: 'averageOrderValue', label: '客单价', className: 'column-secondary' },
  { key: 'cancelRefundAmount', label: '取消退款', className: 'column-tertiary' },
  { key: 'delta', label: '较前日' },
]

function SortIcon({ active, direction }: { active: boolean, direction?: 'asc' | 'desc' }) {
  if (!active) return <ChevronsUpDown aria-hidden="true" />
  return direction === 'asc' ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />
}

function DeltaValue({ current, previous }: { current: number, previous: number }) {
  const rate = deltaRate(current, previous)
  if (rate === null) return <span className="delta-positive"><ArrowUp aria-hidden="true" />新增</span>
  if (!rate) return <span className="delta-neutral">—</span>
  const positive = rate > 0
  return (
    <span className={positive ? 'delta-positive' : 'delta-negative'}>
      {positive ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />}
      {Math.abs(rate).toFixed(2)}%
    </span>
  )
}

export function ProductTable({
  products,
  selectedDate,
  dates,
  sortKey,
  sortDirection,
  onSort,
  compact = false,
  emptyMessage = '没有符合当前筛选条件的商品。',
  metricDates,
  showDelta = true,
  showAverageItemPrice = false,
  badgesByProductId,
  matchTextByProductId,
  profitByProductId,
  profitMarginByProductId,
  profitContextByProductId,
  profitLabel = '含返点利润',
  profitMarginLabel = '含返点利润率',
  erpAvailableByMerchantSku,
  detailSearch = '',
}: ProductTableProps) {
  const previousDate = previousDateOf(dates, selectedDate)
  const visibleColumns = columns.filter((column) => (
    (showDelta || column.key !== 'delta')
    && (showAverageItemPrice || column.key !== 'averageItemPrice')
  ))
  return (
    <div className="table-responsive">
      <table className={compact ? 'product-table is-compact' : 'product-table'}>
        <thead>
          <tr>
            <th className="product-column">商品</th>
            {visibleColumns.map((column) => (
              <Fragment key={column.key}>
                <th className={column.className || ''}>
                  <button type="button" className="sort-button" onClick={() => onSort?.(column.key)} disabled={!onSort}>
                    {column.label}<SortIcon active={sortKey === column.key} direction={sortDirection} />
                  </button>
                </th>
                {profitByProductId && column.key === 'gmv' && (
                  <th className="profit-column" aria-sort={sortKey === 'profitWithRebate' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                    <button type="button" className="sort-button" onClick={() => onSort?.('profitWithRebate')} disabled={!onSort}>
                      {profitLabel}<SortIcon active={sortKey === 'profitWithRebate'} direction={sortDirection} />
                    </button>
                  </th>
                )}
                {profitMarginByProductId && column.key === 'gmv' && (
                  <th className="profit-column" aria-sort={sortKey === 'profitWithRebateMargin' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                    <button type="button" className="sort-button" onClick={() => onSort?.('profitWithRebateMargin')} disabled={!onSort}>
                      {profitMarginLabel}<SortIcon active={sortKey === 'profitWithRebateMargin'} direction={sortDirection} />
                    </button>
                  </th>
                )}
                {erpAvailableByMerchantSku && column.key === 'soldItemCount' && <th>ERP库存</th>}
              </Fragment>
            ))}
            <th aria-label="查看详情" />
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const current = metricDates ? metricsAcrossDates(product, metricDates) : metricsOn(product, selectedDate)
            const previous = metricsOn(product, previousDate)
            const averageItemPrice = itemUnitPrice(current.gmv, current.soldItemCount)
            const merchantSku = product.merchantSku?.trim() || ''
            const erpAvailable = merchantSku ? erpAvailableByMerchantSku?.get(merchantSku) : undefined
            return (
              <tr key={`${product.level}:${product.id}`}>
                <td className="product-cell">
                  <Link to={`/product/${product.level}/${product.id}${detailSearch}`} className="product-link">
                    {product.image ? <img src={product.image} alt="" loading="lazy" /> : <span className="image-fallback">无图</span>}
                    <span className="product-copy">
                      <strong title={product.nameNote ? product.name : undefined}>{product.nameNote || product.name}</strong>
                      <span>{product.level === 'sku'
                        ? `京东SKU ${product.jdSkuId || product.id} · 商家SKU ${product.merchantSku || '未映射'}`
                        : `SPU · ${product.id}`}</span>
                      {!!badgesByProductId?.get(product.id)?.length && (
                        <span className="product-tag-row">
                          {badgesByProductId.get(product.id)?.map((badge) => (
                            <span key={badge.id} className={`tag-badge tag-${badge.tone}`} title={badge.title}>{badge.label}</span>
                          ))}
                        </span>
                      )}
                      {matchTextByProductId?.get(product.id) && <span className="tag-match-text">{matchTextByProductId.get(product.id)}</span>}
                    </span>
                  </Link>
                </td>
                <td className="numeric">{money(current.gmv)}</td>
                {profitByProductId && (
                  <td
                    className={`numeric profit-column ${profitByProductId.get(product.id) === null || profitByProductId.get(product.id) === undefined
                      ? 'profit-missing'
                      : (profitByProductId.get(product.id) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}`}
                    title={profitContextByProductId?.get(product.id)}
                  >
                    {profitByProductId.get(product.id) === null || profitByProductId.get(product.id) === undefined
                      ? '—'
                      : money(profitByProductId.get(product.id) || 0)}
                  </td>
                )}
                {profitMarginByProductId && (
                  <td
                    className={`numeric profit-column ${profitMarginByProductId.get(product.id) === null || profitMarginByProductId.get(product.id) === undefined
                      ? 'profit-missing'
                      : (profitMarginByProductId.get(product.id) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}`}
                    title={profitContextByProductId?.get(product.id)}
                  >
                    {profitMarginByProductId.get(product.id) === null || profitMarginByProductId.get(product.id) === undefined
                      ? '—'
                      : percent((profitMarginByProductId.get(product.id) || 0) * 100)}
                  </td>
                )}
                <td className="numeric">{integer(current.soldItemCount)}</td>
                {erpAvailableByMerchantSku && (
                  <td className="numeric sku-list-erp-inventory" title={erpAvailable === undefined ? '未按商家SKU关联到ERP货品' : '网店管家ERP可订购数量'}>
                    {erpAvailable === undefined ? '—' : integer(erpAvailable)}
                  </td>
                )}
                {showAverageItemPrice && (
                  <td className="numeric">
                    {averageItemPrice === null ? '—' : money(averageItemPrice)}
                  </td>
                )}
                <td className="numeric column-secondary">{integer(current.productVisitors)}</td>
                <td className="numeric">{percent(current.conversionRate)}</td>
                <td className="numeric column-secondary">{money(current.averageOrderValue)}</td>
                <td className="numeric column-tertiary">{money(current.cancelRefundAmount)}</td>
                {showDelta && <td className="numeric"><DeltaValue current={current.gmv} previous={previous.gmv} /></td>}
                <td className="row-action"><Link to={`/product/${product.level}/${product.id}${detailSearch}`} aria-label={`查看${product.name}详情`}><ChevronRight aria-hidden="true" /></Link></td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {!products.length && <div className="empty-state">{emptyMessage}</div>}
    </div>
  )
}
